import { useState } from 'react';
import { toast } from 'react-toastify';
import QuotationServicePicker from '../../ProfilePages/components/QuotationServicePicker';
import { badgeOf } from '../../ServiceDashboard/components/serviceCatalog';
import { formatAUD } from '../../../utils/formatCurrency';
import '../../ProfilePages/styles/QuotationForm.css';
import '../styles/CreateProposalModal.css';

function localId() {
  return `p-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

function tasksTotal(groups) {
  return (groups || []).reduce(
    (groupSum, group) =>
      groupSum +
      (group.tasks || []).reduce(
        (taskSum, task) => taskSum + (Number(task.price) || 0),
        0,
      ),
    0,
  );
}

/** Selected task prices only. The service tier price is not used. */
export function serviceTaskTotal(lines) {
  return (Array.isArray(lines) ? lines : []).reduce(
    (sum, line) => sum + tasksTotal(line.groups),
    0,
  );
}

export function serviceTypeFromLines(lines) {
  const names = [];
  (Array.isArray(lines) ? lines : []).forEach((line) => {
    const label =
      badgeOf({ category: line?.category, service: line?.name, name: line?.name }) ||
      String(line?.name || '').trim();
    if (label && !names.includes(label)) names.push(label);
  });
  return names.join(', ');
}

export function lineFromPick(pick) {
  const groups = new Map();
  (pick.tasks || []).forEach((task) => {
    const name = String(task.groupName || 'Tasks').trim() || 'Tasks';
    if (!groups.has(name)) groups.set(name, { id: localId(), name, tasks: [] });
    groups.get(name).tasks.push({
      id: localId(),
      title: String(task.title || '').trim(),
      scope: String(task.scope || '').trim(),
      price: Number(task.price) || 0,
    });
  });
  const grouped = [...groups.values()];
  return {
    catalogServiceId: pick.catalogId,
    name: pick.name || 'Service',
    description: '',
    category: pick.category || '',
    unitPrice: Number(pick.unitPrice) || 0,
    pricedByTasks: Boolean(pick.pricedByTasks),
    groups: grouped.length ? grouped : [{ id: localId(), name: 'Tasks', tasks: [] }],
    sourceTerms: Array.isArray(pick.termRows) ? pick.termRows : [],
  };
}

export default function ProposalServices({ lines, onChange, onAdd, onRemove }) {
  const [open, setOpen] = useState(false);
  const [activeGroup, setActiveGroup] = useState({});

  const update = (next) => onChange(Array.isArray(next) ? next : []);

  const addPick = (pick) => {
    if ((lines || []).some((line) => line.catalogServiceId === pick.catalogId)) {
      toast.error('That service is already added');
      setOpen(false);
      return;
    }
    if (onAdd) onAdd(pick);
    setOpen(false);
  };

  const patchGroups = (index, groups) => {
    update((lines || []).map((line, i) => (i === index ? { ...line, groups } : line)));
  };

  return (
    <div className="prop-services">
      <div className="prop-services-add">
        <button type="button" onClick={() => setOpen(true)}>
          Add service
        </button>
      </div>
      {(lines || []).map((line, lineIndex) => {
        const groups = line.groups || [];
        const active =
          groups.find((group) => group.id === activeGroup[lineIndex]) || groups[0];
        return (
          <article key={`${line.catalogServiceId}-${lineIndex}`} className="prop-service-card">
            <header>
              <div className="prop-service-heading">
                <strong>{line.name || 'Service'}</strong>
                <span>{formatAUD(tasksTotal(groups))}</span>
              </div>
              <button type="button" onClick={() => onRemove?.(lineIndex)}>
                Remove
              </button>
            </header>
            <div className="prop-service-board">
              <aside>
                {groups.map((group) => (
                  <button
                    key={group.id}
                    type="button"
                    className={group.id === active?.id ? 'is-on' : ''}
                    onClick={() =>
                      setActiveGroup((prev) => ({ ...prev, [lineIndex]: group.id }))
                    }
                  >
                    {group.name}
                    <small>{(group.tasks || []).length}</small>
                  </button>
                ))}
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const name = String(new FormData(e.currentTarget).get('name') || '').trim();
                    if (!name) return;
                    const group = { id: localId(), name, tasks: [] };
                    patchGroups(lineIndex, [...groups, group]);
                    setActiveGroup((prev) => ({ ...prev, [lineIndex]: group.id }));
                    e.currentTarget.reset();
                  }}
                >
                  <input name="name" placeholder="New group" />
                  <button type="submit">Add</button>
                </form>
              </aside>
              <section>
                <div className="prop-service-task-head">
                  <span>{active?.name || 'Tasks'}</span>
                  <button
                    type="button"
                    onClick={() => {
                      if (!active) return;
                      const task = { id: localId(), title: '', scope: '', price: 0 };
                      patchGroups(
                        lineIndex,
                        groups.map((group) =>
                          group.id === active.id
                            ? { ...group, tasks: [...(group.tasks || []), task] }
                            : group,
                        ),
                      );
                    }}
                  >
                    Add task
                  </button>
                </div>
                {(active?.tasks || []).map((task) => (
                  <div key={task.id} className="prop-service-task">
                    <label>
                      Task name
                      <input
                        value={task.title || ''}
                        onChange={(e) => {
                          const title = e.target.value;
                          patchGroups(
                            lineIndex,
                            groups.map((group) =>
                              group.id === active.id
                                ? {
                                    ...group,
                                    tasks: group.tasks.map((row) =>
                                      row.id === task.id ? { ...row, title } : row,
                                    ),
                                  }
                                : group,
                            ),
                          );
                        }}
                      />
                    </label>
                    <label>
                      Client-facing scope
                      <textarea
                        rows={3}
                        value={task.scope || ''}
                        onChange={(e) => {
                          const scope = e.target.value;
                          patchGroups(
                            lineIndex,
                            groups.map((group) =>
                              group.id === active.id
                                ? {
                                    ...group,
                                    tasks: group.tasks.map((row) =>
                                      row.id === task.id ? { ...row, scope } : row,
                                    ),
                                  }
                                : group,
                            ),
                          );
                        }}
                      />
                    </label>
                    <label>
                      Price
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={task.price ?? 0}
                        onChange={(e) => {
                          const price = e.target.value;
                          patchGroups(
                            lineIndex,
                            groups.map((group) =>
                              group.id === active.id
                                ? {
                                    ...group,
                                    tasks: group.tasks.map((row) =>
                                      row.id === task.id ? { ...row, price } : row,
                                    ),
                                  }
                                : group,
                            ),
                          );
                        }}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() =>
                        patchGroups(
                          lineIndex,
                          groups.map((group) =>
                            group.id === active.id
                              ? {
                                  ...group,
                                  tasks: group.tasks.filter((row) => row.id !== task.id),
                                }
                              : group,
                          ),
                        )
                      }
                    >
                      Remove task
                    </button>
                  </div>
                ))}
                {!(active?.tasks || []).length ? <p>No tasks in this group.</p> : null}
              </section>
            </div>
          </article>
        );
      })}
      {open ? (
        <QuotationServicePicker
          persist={false}
          showInstructions={false}
          zIndex={22000}
          onClose={() => setOpen(false)}
          onAdded={addPick}
        />
      ) : null}
    </div>
  );
}
